import { ref, watch, onScopeDispose, type Ref } from 'vue';

/**
 * One IntersectionObserver for the whole board rather than one per token — a
 * board of 40 costs a single observer and a single callback per scroll settle.
 */
type Cb = (visible: boolean) => void;

const callbacks = new WeakMap<Element, Cb>();
let observer: IntersectionObserver | null = null;

function getObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') return null;
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) callbacks.get(entry.target)?.(entry.isIntersecting);
      },
      { rootMargin: '160px' },
    );
  }
  return observer;
}

export function useOnScreen(el: Ref<Element | null>): Ref<boolean> {
  const visible = ref(true);
  let observed: Element | null = null;

  const detach = () => {
    if (!observed) return;
    getObserver()?.unobserve(observed);
    callbacks.delete(observed);
    observed = null;
  };

  watch(
    el,
    (node) => {
      detach();
      const io = getObserver();
      if (!node || !io) return;
      callbacks.set(node, (v) => {
        visible.value = v;
      });
      io.observe(node);
      observed = node;
    },
    { immediate: true },
  );

  onScopeDispose(detach);

  return visible;
}
